import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ApiService from "../services/ApiService";
import Loader from "../components/Loader"; 

const Setting = () => {
  const [isLoader, setIsLoader] = useState(false);
  const [formData, setFormData] = useState({
    free_cancellation_time: 48,
    partial_cancellation_fee_time: 24,
    deposit_percentage: 20,
    partial_cancellation_fee_percentage: 50,
    full_cancellation_fee_percentage: 100,
    stripe_mode: 'test',
  });
  const [companyLogoFile, setCompanyLogoFile] = useState(null);

  useEffect(() => {
    getSingleData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoader(true);
    try {
        const form_data = new FormData();
        Object.keys(formData).forEach((key) => {
            if (key === 'company_logo') {
                return;
            }
            if (key === 'stripe_publishable_key' || key === 'stripe_secret_key') {
                return;
            }
            form_data.append(key, formData[key] || '');
        });

        if (companyLogoFile) {
            form_data.append('company_logo', companyLogoFile);
        }
        const response = await ApiService.request({
            method: "POST",
            url: "admin/settings", 
            data: form_data,
            headers: {
                "Content-Type": "multipart/form-data",
            }
        });
        const data = response.data;
        if (data.status) {
            toast.success(data.message);
            getSingleData(); // Refresh data
            setCompanyLogoFile(null); 
        } else {
            toast.error(data.message);
        }
    } catch (error) {
        console.error(error);
        toast.error('Failed to update settings');
    } finally {
        setIsLoader(false);
    }
  };

  const getSingleData = async () => {
    setIsLoader(true);
    try {
        const response = await ApiService.request({
            method: 'GET',
            url: `admin/settings`,
        });
        const data = response.data;
        if (data.status) {
            // Merge existing settings with default state
            setFormData(prevState => ({
                ...prevState,
                ...data.data
            }));
        } else {
            toast.error(data.message);
        }
    } catch (error) {
        console.error(error);
        toast.error('Failed to load settings');
    } finally {
        setIsLoader(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleFileChange = (name, file) => {
    if (name === 'company_logo') {
      setCompanyLogoFile(file);
    }
  };

  if (isLoader) return <Loader />;

  return (
    <div className="d-flex flex-column flex-column-fluid bg-secondary-dark">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
            <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                    <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                        System Settings
                    </h1>
                </div>
            </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
            <div id="kt_app_content_container" className="app-container container-fluid">
                <div className="card">
                    <div className="card-body pt-5">
                        <form className="form" onSubmit={handleSubmit}>
                            <h3 className="mb-5">Booking & Cancellation</h3>
                            <div className="row mb-6">
                                <div className="col-md-4 mb-3">
                                    <label className="fs-6 fw-semibold mb-2">Free Cancellation Time Limit (Hours)</label>
                                    <input
                                        type="number"
                                        name="free_cancellation_time"
                                        value={formData.free_cancellation_time}
                                        onChange={handleChange}
                                        className="form-control"
                                        placeholder="48"
                                        min="0"
                                    />
                                    <div className="text-muted fs-7">Cancellation before this time results in a full refund of the deposit.</div>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="fs-6 fw-semibold mb-2">Partial Fee Time Limit (Hours)</label>
                                    <input
                                        type="number"
                                        name="partial_cancellation_fee_time"
                                        value={formData.partial_cancellation_fee_time}
                                        onChange={handleChange}
                                        className="form-control"
                                        placeholder="24"
                                        min="0"
                                    />
                                    <div className="text-muted fs-7">Cancellation within {formData.free_cancellation_time} hours but before this time will incur a partial fee.</div>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="fs-6 fw-semibold mb-2">Deposit Percentage (%)</label>
                                    <input
                                        type="number"
                                        name="deposit_percentage"
                                        value={formData.deposit_percentage}
                                        onChange={handleChange}
                                        className="form-control"
                                        placeholder="20"
                                        min="0"
                                        max="100"
                                    />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="fs-6 fw-semibold mb-2">Partial Cancellation Fee (%)</label>
                                    <input
                                        type="number"
                                        name="partial_cancellation_fee_percentage"
                                        value={formData.partial_cancellation_fee_percentage}
                                        onChange={handleChange}
                                        className="form-control"
                                        placeholder="50"
                                        min="0"
                                        max="100"
                                    />
                                    <div className="text-muted fs-7">Fee charged for cancellations between {formData.free_cancellation_time} and {formData.partial_cancellation_fee_time} hours.</div>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="fs-6 fw-semibold mb-2">Full Cancellation Fee (%)</label>
                                    <input
                                        type="number"
                                        name="full_cancellation_fee_percentage"
                                        value={formData.full_cancellation_fee_percentage}
                                        onChange={handleChange}
                                        className="form-control"
                                        placeholder="100"
                                        min="0"
                                        max="100"
                                    />
                                    <div className="text-muted fs-7">Fee charged for cancellations within {formData.partial_cancellation_fee_time} hours.</div>
                                </div>
                            </div>

                            <hr className="my-5" />

                            <h3 className="mb-5">Payment Integration (Stripe)</h3>
                            <div className="row mb-6">
                                <div className="col-md-4 mb-3">
                                    <label className="fs-6 fw-semibold mb-2">Stripe Mode</label>
                                    <select
                                        name="stripe_mode"
                                        value={formData.stripe_mode}
                                        onChange={handleChange}
                                        className="form-select"
                                    >
                                        <option value="test">Test Mode</option>
                                        <option value="live">Live Mode</option>
                                    </select>
                                </div>
                            </div>

                            <hr className="my-5" />

                            <div className="d-flex justify-content-end">
                                <button type="submit" className="btn btn-base" disabled={isLoader}>
                                    {isLoader ? 'Saving...' : 'Save Settings'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
};

export default Setting;
